/* eslint-disable */
import type { TableColumn } from "@repo/core-ui";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { type Control, type FieldArray, useFieldArray, ArrayPath, useFormContext, useWatch } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { EditableCell } from "../components/EditableCell";
import { RowControls } from "../components/RowControls";

import type { FieldValues, ArrayFieldColumn, ActiveCell, RowConfig, RenderControls, CellConfig } from "../types";

interface AdapderOptions<R extends FieldValues> {
  columns: ArrayFieldColumn<R>[];
  control?: Control<R>;
  shouldUnregister?: boolean;
  defaultValues?: R[];
  disabledRowIndexes?: number[];
  disabled?: boolean;
  getRowConfig?: (row: R, index: number) => Partial<RowConfig>;
  getCellConfig?: (col: ArrayFieldColumn<R>, row: R, index: number) => Partial<CellConfig>;
  renderControls?: RenderControls<R>;
  defaultActiveCell?: ActiveCell;
}

const defaultRowConfig = {
  move: true,
  insert: true,
  delete: true,
};

function defaultControlsRenderer<R extends object>(
  _: R,
  index: number,
  methods: { insert: (i: number) => void; remove: (i: number) => void },
  DefaultControls: FunctionComponent<{ index: number; onInsert: (i: number) => void; onDelete: (i: number) => void }>
) {
  return <DefaultControls index={index} onDelete={methods.remove} onInsert={methods.insert} />;
}

function getDefaultValues<R extends FieldValues>(columns: ArrayFieldColumn<R>[]) {
  return columns.reduce((acc, col) => ({ ...acc, [col.field]: col.defaultValue ?? "" }), {});
}

export function useTableAdapter<R extends FieldValues, N extends string>(
  name: N,
  {
    columns,
    control,
    shouldUnregister,
    defaultValues,
    defaultActiveCell = {},
    disabled = false,
    getRowConfig = () => defaultRowConfig,
    getCellConfig,
    renderControls = defaultControlsRenderer,
  }: AdapderOptions<R> = {
    columns: [],
  }
) {
  const { fields, append, insert, remove, move, replace } = useFieldArray({ keyName: "id", name: name as ArrayPath<R>, control });
  const data = useWatch({ name });
  const { trigger } = useFormContext();
  const [activeCell, setActiveCell] = useState<ActiveCell>(defaultActiveCell);
  const rows = useMemo(() => fields.map((field, i) => ({ ...field, ...data?.[i] }) as R), [fields, data]);

  useEffect(function setDefaultValues() {
    if (defaultValues && fields.length === 0) replace(defaultValues as FieldArray<R, ArrayPath<R>>);
  }, []);

  function appendRow() {
    const rowIndex = fields.length;
    const [col] = columns;

    const row = getDefaultValues(columns);
    append(row as FieldArray<R, ArrayPath<R>>);
    setActiveCell({ columnName: col?.field, rowIndex });
    void trigger(`${name}.${rowIndex}`);
  }

  function insertRow(rowIndex: number) {
    const [col] = columns;

    const row = getDefaultValues(columns);
    insert(rowIndex, row as FieldArray<R, ArrayPath<R>>);
    setActiveCell({ columnName: col?.field, rowIndex });
    void trigger(`${name}.${rowIndex}`);
  }

  function removeRow(index: number) {
    remove(index);

    if (index === activeCell.rowIndex) {
      setActiveCell({});
    }
  }

  function moveRow(id: string, atIndex: number) {
    const index = fields.findIndex(row => row.id === id);
    move(index, atIndex);
  }

  const tableColumns = columns.map((col, colIndex): TableColumn<R> => {
    const { field: fieldName, renderField, renderValue, cellClassName, headerCellClassName, align, editable = true, defaultValue = "", ...commonProps } = col;

    return {
      field: fieldName,
      headerCellClassName: twMerge("whitespace-nowrap", headerCellClassName),
      cellClassName: twMerge("h-[3.25rem] whitespace-nowrap", cellClassName),
      align,
      renderCell: (row: R, index: number) => {
        const rowConfig = !disabled
          ? {
              ...defaultRowConfig,
              ...getRowConfig(row, index),
            }
          : { move: false, insert: false, delete: false };

        const cellConfig = !disabled
          ? {
              editable,
              ...getCellConfig?.(col, row, index),
            }
          : { editable: false };

        const controls = renderControls(row, index, { remove: removeRow, insert: insertRow }, RowControls);

        function handleCellClick() {
          if (!cellConfig.editable) return;

          setActiveCell({ columnName: fieldName, rowIndex: index });
        }

        return (
          <EditableCell
            path={name}
            row={row}
            editable={cellConfig.editable && activeCell.columnName === fieldName && activeCell.rowIndex === index && !disabled}
            rowId={row["id" as keyof R] as string}
            rowIndex={index}
            colIndex={colIndex}
            name={fieldName}
            defaultValue={defaultValue}
            control={control}
            shouldUnregister={shouldUnregister}
            rowConfig={rowConfig}
            cellConfig={cellConfig}
            rowControls={controls}
            textAlign={align}
            move={moveRow}
            renderValue={renderValue}
            renderField={renderField}
            onEdited={() => {
              setActiveCell({});
            }}
            onClick={handleCellClick}
          />
        );
      },
      ...commonProps,
    };
  });

  return {
    columns: tableColumns,
    rows,
    appendRow,
    insertRow,
    removeRow,
  };
}
